import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  GmailConnectionStatus, 
  EmailMetadata, 
  EmailDetailData, 
  EmailFilters, 
  EmailComposeData
} from '../types/gmail';

export function useGmail() {
  const { user, viewingAsUserId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the effective user ID (for viewing as another user)
  const getEffectiveUserId = useCallback(() => viewingAsUserId || user?.id || null, [viewingAsUserId, user?.id]);

  // Check Gmail connection status
  const checkConnection = useCallback(async (): Promise<GmailConnectionStatus> => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      return { isConnected: false };
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/.netlify/functions/gmail-refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: effectiveUserId }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { isConnected: false };
      }

      return {
        isConnected: true,
        emailAddress: data.email_address,
        tokenExpiry: data.token_expiry,
      };
    } catch (err) {
      console.error('Error checking Gmail connection:', err);
      return { isConnected: false };
    } finally {
      setLoading(false);
    }
  }, [getEffectiveUserId]);

  // Connect Gmail (initiate OAuth flow)
  const connectGmail = useCallback(async (): Promise<{ authUrl: string; state: string } | null> => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setError('User not authenticated');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/.netlify/functions/gmail-oauth-init', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: effectiveUserId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to initiate Gmail connection');
      }

      return {
        authUrl: data.authUrl,
        state: data.state,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect Gmail';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getEffectiveUserId]);

  // Complete OAuth flow
  const completeConnection = useCallback(async (code: string, state: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/.netlify/functions/gmail-oauth-callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, state }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to complete Gmail connection');
      }

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete Gmail connection';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Disconnect Gmail
  const disconnectGmail = useCallback(async (): Promise<boolean> => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setError('User not authenticated');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/.netlify/functions/gmail-disconnect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: effectiveUserId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to disconnect Gmail');
      }

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to disconnect Gmail';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getEffectiveUserId]);

  // List emails
  const listEmails = useCallback(async (filters: EmailFilters = {}): Promise<{emails: EmailMetadata[], nextPageToken?: string}> => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setError('User not authenticated');
      return {emails: []};
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/.netlify/functions/gmail-list-emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: effectiveUserId,
          ...filters,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch emails');
      }

      return {
        emails: data.emails || [],
        nextPageToken: data.nextPageToken
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch emails';
      setError(errorMessage);
      return {emails: []};
    } finally {
      setLoading(false);
    }
  }, [getEffectiveUserId]);

  // Get email detail
  const getEmail = useCallback(async (emailId: string): Promise<EmailDetailData | null> => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setError('User not authenticated');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/.netlify/functions/gmail-get-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: effectiveUserId,
          emailId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch email details');
      }

      return data.email || null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch email details';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getEffectiveUserId]);

  // Send email
  const sendEmail = useCallback(async (
    composeData: EmailComposeData,
    contactId?: string
  ): Promise<boolean> => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setError('User not authenticated');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/.netlify/functions/gmail-send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: effectiveUserId,
          contactId,
          ...composeData,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send email');
      }

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send email';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getEffectiveUserId]);

  // Sync email to event
  const syncEmailToEvent = useCallback(async (
    emailId: string,
    contactId?: string
  ): Promise<boolean> => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setError('User not authenticated');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/.netlify/functions/gmail-sync-to-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: effectiveUserId,
          emailId,
          contactId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync email to event');
      }

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sync email to event';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getEffectiveUserId]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    checkConnection,
    connectGmail,
    completeConnection,
    disconnectGmail,
    listEmails,
    getEmail,
    sendEmail,
    syncEmailToEvent,
    clearError,
  };
}
